import {
  object,
  number,
  type InferOutput,
  enum_,
  string,
  picklist,
  optional,
  isoDate,
  pipe,
  array,
  literal,
  boolean,
  union
} from 'valibot'

import {
  createSorterSchema,
  SEARCH_DATE_BASIS,
  SHIPMENT_ORDERS_SORT_KEYS
} from '@/features/shared/models'
import {
  SHIPMENT_DELIVERY_STATUS_TYPE,
  type ShipmentOrdersRequestQuery
} from '@/libs/api/types'

// isoDate()もしくは空文字を許容
const isoDateOrEmpty = union([pipe(string(), isoDate()), literal('')])

/**
 * 配送依頼一覧の検索条件
 */
export const shipmentOrdersSearchSchema = object({
  page: optional(number()),
  take: optional(number()),
  ...createSorterSchema(SHIPMENT_ORDERS_SORT_KEYS),
  /** 配送ステータス (複数指定可) */
  status: optional(array(enum_(SHIPMENT_DELIVERY_STATUS_TYPE))),
  /** 配送依頼No */
  orderNo: optional(string()),
  /** 発送日 From */
  shippingDateFrom: optional(isoDateOrEmpty),
  /** 発送日 To */
  shippingDateTo: optional(isoDateOrEmpty),
  /** 着日 From */
  deliveryDateFrom: optional(pipe(string(), isoDate())),
  /** 着日 To */
  deliveryDateTo: optional(pipe(string(), isoDate())),
  /**
   * 登録日 From（#5285 VOC_PJ-506）。
   * 12ヶ月幅への補完は検索フォームの submit でだけ行う。片側だけの URL を直接開いた場合は
   * 補完されず BE が 400 を返し、loader の失敗としてページ全体のエラーになる（画面の操作では
   * 常に両側そろった URL になるため、手で組んだ URL に限られる）
   */
  createdAtFrom: optional(isoDateOrEmpty),
  /** 登録日 To */
  createdAtTo: optional(isoDateOrEmpty),
  /**
   * 検索日付基準（省略時 DEFAULT = 既存の暦日検索。VOC_PJ-939 #4387）。
   * 配送依頼一覧は支払月軸を扱わないため 2 値に限定する。共通 enum をそのまま受理すると、
   * URL 直打ちで支払月が入り、UI に表示されない値のまま API が 400 を返す。
   */
  searchDateBasis: optional(
    picklist([SEARCH_DATE_BASIS.DEFAULT, SEARCH_DATE_BASIS.INCOME_CLOSING_DATE])
  ),
  /** 計上月（yyyy-MM-dd に正規化。締日基準検索時のみ送る） */
  operatingYearMonth: optional(isoDateOrEmpty),
  /** 荷主ID */
  customerIds: optional(array(number())),
  /** パートナーID **/
  partnerIds: optional(array(number())),
  /** 配送種別ID */
  shipmentCategoryIds: optional(array(number())),
  /** 発地 地点ID */
  originSiteIds: optional(array(number())),
  /** 着地 地点ID */
  destSiteIds: optional(array(number())),
  /** 拠点(物流センター)ID */
  dcId: optional(number()),
  /** 配車済を含む
   * - true のみ指定可能（falseは使わない）
   */
  includeDispatched: optional(literal(true)),
  /** 配送依頼作成方法 (true: 手入力のみ, false: インポートのみ) */
  isManual: optional(boolean()),
  /** 配車パートナーID */
  dispatchPartnerIds: optional(array(number())),
  /** 配車運送会社ID */
  dispatchSubPartnerIds: optional(array(number())),
  /** 配車車両番号 */
  dispatchTruckCode: optional(string()),
  /** カスタムキー横断フリーワード検索（customKey1〜5 のいずれかに部分一致。VOC_PJ-960 #4487） */
  customKeyFreeWord: optional(string())
})

export type ShipmentOrdersSearch = InferOutput<
  typeof shipmentOrdersSearchSchema
>

/**
 * 登録日での絞り込みが指定されているか（#5285 VOC_PJ-506）。
 *
 * 片側だけでも真を返す。片側指定は BE の登録日の検証が弾くため、ここでは「登録日軸で
 * 検索する意図があるか」だけを見る。空文字は URL 上の未指定（検索フォームのセンチネル）
 * なので指定なしとして扱う。
 *
 * BE の hasCreatedAtRange は `!== undefined` で判定するので規則は一致しない。空文字を
 * 直接送ると BE では Invalid Date になって「指定あり」と見なされ、発送日の必須が外れた
 * うえで登録日の検証に落ちる。queryFrom が空文字を落とすので画面経由では起きない（D-42）。
 */
export const hasCreatedAtRange = (search: ShipmentOrdersSearch): boolean =>
  Boolean(search.createdAtFrom || search.createdAtTo)

export const queryFrom = (
  search: ShipmentOrdersSearch
): NonNullable<ShipmentOrdersRequestQuery> => {
  const {
    dcId,
    shippingDateFrom,
    shippingDateTo,
    orderNo,
    status,
    createdAtFrom,
    createdAtTo,
    ...rest
  } = search
  return {
    ...rest,
    deliveryStatus: status,
    includeDispatched: true,
    dcIds: [dcId!],
    orderNos: orderNo ? [orderNo] : undefined,
    shippingDateFrom: shippingDateFrom || undefined,
    shippingDateTo: shippingDateTo || undefined,
    // 空文字は未指定として落とす（BE は登録日を両側必須で検証するため片側の空文字で 400 になる）
    createdAtFrom: createdAtFrom || undefined,
    createdAtTo: createdAtTo || undefined
  }
}
